import React, {FC, Fragment, useState, useEffect} from 'react';
import OpenAI from 'openai';
import {Emit_main} from "../navigation";
import {Eres_wizard_button} from "../controls";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faSyncAlt} from "@fortawesome/free-solid-svg-icons"

interface IOpenAI {
    chat: {
        completions: {
            create: (params: { messages: IMessage[]; model: string }) => Promise<any>;
        };
    };
}

interface IMessage {
    role: 'user' | 'system' | 'assistant';
    content: string;
}

interface IImageResponse {
    data: {
        url: string;
    }[];
}
class OpenAIClient {
    private apiKey: string;
    private client!: IOpenAI;

    constructor(apiKey: string) {
        if (!apiKey) {
            throw new Error("API key is not set in environment variables");
        }
        this.apiKey = apiKey;
        this.client = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
    }

    public async createChatCompletion(messages: IMessage[], model: string): Promise<any> {
        return await this.client.chat.completions.create({
            messages,
            model,
        });
    }

    public async createImage(prompt: string): Promise<string> {
        const response = await fetch('https://api.openai.com/v1/images/generations', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.apiKey}`
            },
            body: JSON.stringify({
                model: "dall-e-3",
                prompt: prompt,
                n: 1,
                size: "1024x1024"
            })
        });

        const data: IImageResponse = await response.json();
        return data.data[0].url;
    }
}


const Emit_generator: FC = () => {
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [client, setClient] = useState<OpenAIClient | null>(null);

    useEffect(() => {
        const apiKey = "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";
        const newClient = new OpenAIClient(apiKey);
        setClient(newClient);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const parseAssistantMessage = (message: string): IMessage[] => {
        const pairs = message.split(/\n\n+/);
        return pairs.map((pair, index) => {
            return { content: `#${index + 1} ${pair}`, role: 'assistant' };
        });
    };

    const regenerateImage = async () => {
        if (!client) return;

        setIsLoading(true);
        try {
            const img_prompt = `Create a background image... for ${input}`;
            const imageResponseUrl = await client.createImage(img_prompt);
            setImageUrl(imageResponseUrl);
        } catch (error) {
            console.error("Error regenerating image:", error);
        }
        setIsLoading(false);
    };

    const handleSend = async () => {
        if (!client) return;

        setIsLoading(true);
        try {
            const our_prompt = `You are an innovative copywriter for the best advertising agency. ${input}. What 5 different Headline and Copy pairs do you provide for their Facebook ads so it is effective but cool? Then say nothing else.`;
            const chatResponse = await client.createChatCompletion(
                [{ role: "user", content: our_prompt }],
                "gpt-3.5-turbo"
            );
            const img_prompt = `Create a background image that is optimized for a high-converting Facebook ad for ${input}. Do not use any letters or text inside of the image. The image should be 1024x1024 pixels.`;
            const imageResponseUrl = await client.createImage(img_prompt);

            setMessages(messages => [...messages, { role: "user", content: input }]);
            const assistantResponse = chatResponse.choices[0].message.content;
            const parsedMessages = parseAssistantMessage(assistantResponse);
            setMessages(messages => [...messages, ...parsedMessages]);

            setImageUrl(imageResponseUrl);
            setInput('');
        } catch (error) {
            console.error("An error occurred:", error);
        }
        setIsLoading(false);
    };

    return (
        <Fragment>
            <h3 className="tw-text-xl md:tw-text-2xl tw-font-bold tw-py-4">Ad Generation Wizard</h3>
            <div className="tw-flex tw-flex-col">
                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-w-full">
                    {isLoading ? (
                        <div className="tw-relative tw-items-center tw-block tw-w-full tw-p-6 tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-shadow-md tw-dark:bg-gray-800 tw-dark:border-gray-800 tw-dark:hover:bg-gray-700 tw-min-h-[300px]">
                            <div role="status" className="tw-absolute tw--translate-x-1/2 tw--translate-y-1/2 tw-top-2/4 tw-left-1/2">
                                <svg aria-hidden="true" className="tw-w-8 tw-h-8 tw-text-gray-200 tw-animate-spin tw-dark:text-gray-600 tw-fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                </svg>
                                <span className="tw-sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="tw-w-full tw-p-4 tw-bg-white tw-shadow-lg tw-rounded-lg">
                            <div className="tw-py-2 tw-px-4 tw-bg-gray-100 tw-rounded-t-lg">
                                <p className="tw-text-sm md:tw-text-base tw-text-gray-800">Input example: eres.io is a platform that empowers users to create ads that will scale their business.</p>
                            </div>
                            <div className="tw-p-4 tw-flex tw-flex-col tw-gap-3">
                                <input type="text" value={input} onChange={handleInputChange} className="tw-w-full tw-p-2 tw-border tw-rounded tw-border-gray-300" />
                                <Eres_wizard_button onClick={handleSend}>Generate</Eres_wizard_button>
                            </div>
                        </div>
                    )}
                    <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-2 sm:tw-p-4 tw-max-h-[500px] lg:tw-max-h-[75vh] tw-overflow-y-auto tw-overflow-x-hidden">
                        {messages.map((message, index) => (
                            <div key={index}
                                 className={`tw-mb-4 tw-p-3 sm:tw-p-4 tw-rounded-lg tw-bg-${message.role === 'user' ? 'blue-100' : 'green-100'} tw-border tw-border-gray-200 tw-break-words`}>
                                <p className={`tw-text-sm md:tw-text-base tw-text-${message.role === 'user' ? 'blue-800' : 'green-800'} tw-font-semibold`}>{message.content}</p>
                            </div>
                        ))}
                        <div className="tw-relative">
                            {imageUrl && (
                                <div className="tw-relative">
                                    <img src={imageUrl} alt="Generated" className="tw-w-full tw-h-auto tw-rounded-lg"/>
                                    <button onClick={regenerateImage}
                                            className="tw-absolute tw-top-2 tw-right-2 tw-bg-gray-800 tw-bg-opacity-50 hover:tw-bg-opacity-70 tw-p-2 tw-rounded-full tw-transition-all">
                                        <FontAwesomeIcon icon={faSyncAlt} area-hidden="true" className="tw-text-white"/>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default function generator({}) {
    return <Emit_main page_id="PAGE-generator"><Emit_generator/></Emit_main>;
}
